import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  from,
  interval,
  map,
  mapTo,
  Observable,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { Authorization } from "../../common/AuthorizationCpt";
import {
  BodyContainerTableCommon,
  ContainerConfigurateCommon,
  ContainerGridColumnsTableCommon,
  ContainerScrollCommon,
  DivStatusCommon,
  TableHeaderCommonResponsive,
} from "../../common/components/TableResponsive";
import { getErrorValueResponse } from "../../common/error/Error";
import { generateSequentialArray } from "../../common/utils";
import { HTTP_METHODS } from "../../utils/constants";
import { networkBackendRequest } from "../../utils/networkUtils";
import { ITEMS_PER_PAGE } from "../../utils/valueStaticConstants";
import AdminAskForAction from "../AdminAskForAction/AdminAskForAction";
import { SubtitleDiv } from "../AdminDocumentManagement/AdminDocumentManagementSharedStyles";
import AdminPaymentValidationSidebar from "../AdminPaymentValidationSidebar/AdminPaymentValidationSidebar";
import {
  ContainerConfigurate,
  ContainerScroll,
  DivDetailTitle,
} from "../AdminPaymentValidationSidebar/AdminPaymentValidationSidebarStyles";
import { AdminPaymentValidationStatesSidebar } from "../AdminPaymentValidationStatesSidebar/AdminPaymentValidationStatesSidebar";
import { InfoSelectPlanel } from "../AdminPaymentValidationStatesSidebar/modals/infoSelectPanel";
import {
  InvoiceContainer,
  InvoicesContainer,
  TableHeaderCommon,
} from "../AdminPaymentValidationTable/CommonTable";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  AmountInfo,
  FakeRow,
} from "../DocumentManagement/DocumentManagementSharedStyles";
import RejectionComment from "../RejectionComment/RejectionComment";
import Sidebar from "../Sidebar/Sidebar";
import StandaloneMessage from "../StandaloneMessage/StandaloneMessage";
import {
  AdminPaymentValidationParams,
  InvoiceConvert,
} from "./AdminPaymentValidation.interface";
import { ConvertResponseAdminPaymentValidate } from "./ConvertResponseAdminPaymentValidate";

interface Props {
  onMessageConfirmationChange: (message: string) => void;
  onTitleConfirmationChange: (title: string) => void;
  togglePaymentConfirmation: () => void;
  params$: Observable<AdminPaymentValidationParams>;
  setShowLabelPaymentNum: React.Dispatch<React.SetStateAction<string>>;
}

const AdminPaymentValidationTable = (props: Props) => {
  const {
    onMessageConfirmationChange,
    onTitleConfirmationChange,
    togglePaymentConfirmation,
    params$,
    setShowLabelPaymentNum,
  } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;
  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const loadAdminPaymentUpfront = useAsyncExecutor2(
    (params) => {
      const paramsNew = {
        bank_option: params.bank_type.length > 0 ? params.bank_type : [],
        payment_status_option:
          params.payment_status_option.length > 0
            ? params.payment_status_option
            : [],
        cashback_status_option:
          params.cashback_status_option.length > 0
            ? params.cashback_status_option
            : [],
        differential_status_option:
          params.differential_status_option.length > 0
            ? params.differential_status_option
            : [],
        volante_nums: params.volante_nums.length > 0 ? params.volante_nums : [],
        agent_name: params.agent_name,
        from_date: params.from_date,
        to_date: params.to_date,
        order: params.order,
        page: params.page,
        page_size: params.page_size,
      };

      const destroy$ = destroy$Ref.current;
      return interval(10000).pipe(
        takeUntil(destroy$),
        startWith(paramsNew),
        mapTo(paramsNew),
        switchMap((params) => {
          const request = networkBackendRequest(
            `/invoices/upfront-manual/payment-status/?${stringify(params)}`,
            HTTP_METHODS.GET,
            undefined,
            undefined
          )
            .then((response) => response.json())
            .then(ConvertResponseAdminPaymentValidate);
          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        map((params) => ({
          ...params,
          from_date: format(params.from_date, "yyyy-MM-dd"),
          to_date: format(params.to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
        }))
      ),
      onError: () => {},
    }
  );

  const auxiliaryArray = generateSequentialArray(ITEMS_PER_PAGE);

  const [showSidebar, updateShowSidebar] = useState(false);
  const [sidebarInfo, updateSidebarInfo] = useState<InvoiceConvert>({
    agentName: "",
    invoiceNum: "",
    vouchers: [],
  });
  const [showRejectionComment, updateShowRejectionComment] = useState(false);
  const [selectedElement, updateSelectedElement] =
    useState<InvoiceConvert>(null);
  const [selectedStatus, updateSelectedStatus] = useState<string>(null);
  const [listVouchers, updateListVouchers] = useState<string[]>([]);
  const [showAskForAction, updateAskForAction] = useState(false);
  const toggleShowRejectionComment = () => {
    updateShowRejectionComment(!showRejectionComment);
  };
  const handleRejectionComment = (elem: InvoiceConvert, newStatus: string) => {
    toggleShowRejectionComment();
    updateSelectedElement(elem);
    updateSelectedStatus(newStatus);
  };
  const handleClick = (newSidebarInfo: InvoiceConvert) => {
    updateSidebarInfo(newSidebarInfo);
    updateShowStateSidebar(true);
  };
  const toggleShowAskForAction = () => {
    updateAskForAction(!showAskForAction);
  };
  const handleAskForAction = (
    elem: InvoiceConvert,
    vouchers: any,
    newStatus: string
  ) => {
    toggleShowAskForAction();
    updateSelectedElement(elem);
    updateListVouchers(vouchers);
    updateSelectedStatus(newStatus);
  };
  const [messageError, updateMessageError] = useState("");
  const [showModalError, updateShowModalError] = useState(false);

  const toggleGetModal = () => {
    updateShowModalError(!showModalError);
  };

  const approveRejectDocuments = (
    elem: InvoiceConvert,
    vouchers: any,
    newStatus: string,
    comment: string = null
  ) => {
    const upfront_manual_payment_details =
      vouchers.length === 0
        ? vouchers
        : vouchers.operations.map((operation) => {
            const dateOperation = new Date(operation.payment_date);
            const date = format(dateOperation, "yyyy-MM-dd");
            return {
              voucher_id: operation.id,
              operation_number: operation.line.numeroOperacion,
              amount: +operation.montoDeposito,

              operation_number_voucher: operation.operation_number_voucher,
              payment_date: date,
            };
          });
    return networkBackendRequest(
      `/invoices/upfront-manual/${elem.invoiceId}/`,
      HTTP_METHODS.PATCH,
      null,
      {
        upfront_manual_payment_details,
        payment_status_option: newStatus,
        invoice_rejected_comment: newStatus === "rejected" ? comment : null,
      }
    )
      .then(() => {
        if (showRejectionComment) toggleShowRejectionComment();
        if (showSidebar) updateShowSidebar(false);
        if (showStateSidebar) updateShowStateSidebar(false);
        togglePaymentConfirmation();
        onTitleConfirmationChange(
          newStatus === "paid" ? "Pago validado" : "Pago rechazado"
        );
        onMessageConfirmationChange(
          newStatus === "paid"
            ? `Se ha validado el ${elem.invoiceNum} por ${
                elem.paymentCurrency === "usd"
                  ? `${elem.salesInvoiceTotal.usd} dólares`
                  : `${elem.salesInvoiceTotal.pen} soles`
              }.`
            : `Se ha rechazado el`
        );
        setShowLabelPaymentNum(
          newStatus === "paid"
            ? `pago N° ${elem.invoiceNum} por ${
                elem.paymentCurrency === "usd"
                  ? `${elem.salesInvoiceTotal.usd} dólares`
                  : `${elem.salesInvoiceTotal.pen} soles`
              }.`
            : `pago N° ${elem.invoiceNum}`
        );
      })
      .catch((error) => {
        const errorMessage = getErrorValueResponse(error);
        updateMessageError(errorMessage);
        toggleGetModal();
      });
  };

  const respData = loadAdminPaymentUpfront.data;
  const [showSidebarModal, setShowSidebarModal] = useState(false);
  const closeSideInfo = () => {
    setShowSidebarModal(!showSidebarModal);
  };

  const [showStateSidebar, updateShowStateSidebar] = useState(false);

  const gridTemplateColumnsSidebar = "1.2fr 1.5fr 1fr 1fr";
  const gridTemplateColumnsSidebar2 = "1fr 1.5fr 2fr 1fr 1fr 1fr 1fr 1fr";
  const history = useHistory();

  const formatDate = (date: Date) => {
    const dateNew = format(new Date(date), "dd/MM/yyyy");
    return dateNew;
  };

  const formatDateSecond = (date: Date) => {
    const dateSecondnew = format(new Date(date), "HH:mm:ss");
    return dateSecondnew;
  };
  return (
    <div>
      {loadAdminPaymentUpfront.isError && (
        <div>
          Algo salió mal
          <button
            type="button"
            onClick={() => {
              loadAdminPaymentUpfront.retry();
            }}
          >
            Reintentar
          </button>
        </div>
      )}
      {showModalError && messageError && (
        <ConfirmationModal
          message={messageError}
          onAccept={() => {
            toggleGetModal();
          }}
          title={`Atención`}
          toggleModal={() => {
            toggleGetModal();
          }}
        />
      )}
      {loadAdminPaymentUpfront.isLoading &&
        auxiliaryArray.map((_, index) => {
          return <FakeRow key={index} />;
        })}

      {loadAdminPaymentUpfront.isSuccess && (
        <div>
          {respData.data.length === 0 && (
            <StandaloneMessage>
              No se encontraron pagos a validar con los parámetros brindados.
            </StandaloneMessage>
          )}

          {respData.data.length > 0 && (
            <Authorization
              actions={[
                "invoice.get_invoice_upfront_manual",
                "invoice.update_invoice_upfront_manual",
              ]}
            >
              <div>
                <ContainerScrollCommon>
                  <ContainerConfigurateCommon>
                    <ContainerGridColumnsTableCommon
                      gridTemplateColumns={gridTemplateColumnsSidebar2}
                    >
                      <TableHeaderCommonResponsive className="item-container">
                        <div
                          className="main-info-header"
                          style={{ marginLeft: "2rem" }}
                        >
                          {"ID PAGO"}
                        </div>
                        <div>{"AGENTE DE ADUANA"}</div>
                        <div>{"FECHA/HORA DE SUBIDA"}</div>
                        <div>{"BANCO"}</div>
                        <div>{"MONTO"}</div>
                        <div>{"ESTADO DE PAGO"}</div>
                        <div>{"SUBESTADO"}</div>
                        <div>{"SALDO"}</div>
                      </TableHeaderCommonResponsive>
                      <div
                        style={{ overflowY: "scroll", maxHeight: "90vh" }}
                        className="scroll"
                      >
                        {respData.data.map((elem) => {
                          return (
                            <div
                              key={elem.invoiceNum}
                              style={{ cursor: "pointer" }}
                            >
                              <BodyContainerTableCommon
                                className="item-container"
                                height="8rem"
                              >
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    height: "100%",
                                  }}
                                >
                                  <div className="titles">{"ID PAGO"}</div>
                                  <div>{elem.invoiceNum}</div>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">
                                    {"AGENTE DE ADUANA"}
                                  </div>
                                  <div className="left-align">
                                    {elem.agentName}
                                  </div>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">
                                    {"FECHA/HORA DE SUBIDA"}
                                  </div>
                                  <div>
                                    <b>
                                      {formatDate(new Date(elem.dateInvoice))}
                                    </b>
                                    <div>
                                      {formatDateSecond(
                                        new Date(elem.dateInvoice)
                                      )}{" "}
                                    </div>
                                  </div>
                                </div>

                                <div className="attribute">
                                  <div className="titles">{"BANCO"}</div>
                                  <InfoSelectPlanel
                                    fontSizeCounLabel={"1.4rem"}
                                    iconCountWith={"2.5rem"}
                                    iconCountHeight={"2.5rem"}
                                    items={elem.vouchers.map(
                                      (voucher) => voucher.bank_name.name
                                    )}
                                    height={"3.2rem"}
                                    widthPanel={"28.1rem"}
                                    bankType="table-bank"
                                  ></InfoSelectPlanel>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">{"MONTO"}</div>
                                  <AmountInfo>
                                    <div>{elem.currencyPayment}</div>
                                    <div>{`(${
                                      elem.paymentCurrency === "usd"
                                        ? "DÓLARES"
                                        : "SOLES"
                                    })`}</div>
                                  </AmountInfo>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">
                                    {"ESTADO DE PAGO"}
                                  </div>
                                  <DivStatusCommon
                                    status={elem.paymentStatus.category}
                                  >
                                    <div className="type-column-common">
                                      <b>
                                        {elem.paymentStatus.label.toUpperCase()}
                                      </b>
                                    </div>
                                  </DivStatusCommon>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">{"SUBESTADO"}</div>
                                  <DivStatusCommon
                                    status={elem.cashbackStatus.category}
                                  >
                                    <div className="type-column-common">
                                      <b>
                                        {elem.cashbackStatus.label.toUpperCase()}
                                      </b>
                                    </div>
                                  </DivStatusCommon>
                                </div>
                                <div
                                  className="attribute"
                                  onClick={() => handleClick(elem)}
                                  style={{ height: "100%" }}
                                >
                                  <div className="titles">{"SALDO"}</div>
                                  <DivStatusCommon
                                    status={elem.differentialStatus.category}
                                  >
                                    <div className="type-column-common">
                                      <b>
                                        {elem.differentialStatus.label.toUpperCase()}
                                      </b>
                                    </div>
                                  </DivStatusCommon>
                                </div>
                              </BodyContainerTableCommon>
                            </div>
                          );
                        })}
                      </div>
                    </ContainerGridColumnsTableCommon>
                  </ContainerConfigurateCommon>
                </ContainerScrollCommon>
                {loadAdminPaymentUpfront.status === "SUCCESS" &&
                  loadAdminPaymentUpfront.data.data.length > 0 && (
                    <div className="flex-paginator">
                      <Paginator
                        first={
                          (loadAdminPaymentUpfront.data.page - 1) *
                          loadAdminPaymentUpfront.data.page_size
                        }
                        rows={loadAdminPaymentUpfront.data.page_size}
                        totalRecords={loadAdminPaymentUpfront.data.count}
                        onPageChange={(e) => {
                          const targetPage =
                            e.first / loadAdminPaymentUpfront.data.page_size;
                          const pathname = history.location.pathname;
                          const parsedParams = parse(history.location.search);
                          const stringifiedParams = stringify({
                            ...parsedParams,
                            page: targetPage + 1,
                          });
                          history.push(`${pathname}?${stringifiedParams}`);
                        }}
                      />
                    </div>
                  )}
              </div>
            </Authorization>
          )}
        </div>
      )}

      {showSidebarModal && (
        <Sidebar onOutsideClick={closeSideInfo}>
          <ContainerScroll>
            <ContainerConfigurate width="89.2rem">
              <DivDetailTitle fontSize="2rem">Detalles del pago</DivDetailTitle>
              <InvoicesContainer
                gridTemplateColumns={gridTemplateColumnsSidebar}
              >
                <TableHeaderCommon className="item-container">
                  <div
                    className="main-info-header"
                    style={{ marginLeft: "2rem" }}
                  >
                    {"DAM"}
                  </div>
                  <div>{"RUC / RAZÓN SOCIAL FACTURABLE"}</div>
                  <div>{"HAWB / AWB"}</div>
                  <div>{"VOLANTE / PREFACTURA"}</div>
                </TableHeaderCommon>
                <div
                  style={{ overflowY: "scroll", maxHeight: "21.1vh" }}
                  className="scroll"
                >
                  {sidebarInfo &&
                    sidebarInfo.volantes.map((elem) => {
                      return (
                        <div key={elem.dam_num} style={{ cursor: "pointer" }}>
                          <InvoiceContainer
                            className="item-container"
                            height={"auto"}
                          >
                            <div className="attribute main-info-header">
                              <div className="titles">{"DAM"}</div>
                              <div className="main-info-header">
                                <div className="left-align">{elem.dam_num}</div>
                              </div>
                            </div>
                            <div
                              className="attribute"
                              style={{ justifyContent: "start" }}
                            >
                              <div className="titles">{"RUC RAZÓN SOCIAL"}</div>
                              <div>{elem.billable_ruc}</div>
                              <div
                                style={{ opacity: 0.7, textAlign: "center" }}
                              >
                                {elem.billable_ruc_name}
                              </div>
                            </div>
                            <div className="attribute">
                              <div className="titles">{"HAWB"}</div>
                              <div className="left-align">
                                {elem.house_air_waybill}
                              </div>
                              <div className="titles">{"AWB"}</div>
                              <div style={{ opacity: 0.7 }}>
                                {elem.master_air_waybill}
                              </div>
                            </div>
                            <div className="attribute">
                              <div className="titles">{"VOLANTE"}</div>
                              <SubtitleDiv>{elem.volante_num}</SubtitleDiv>
                              <SubtitleDiv style={{ opacity: 0.7 }}>
                                {elem.factura_num}
                              </SubtitleDiv>
                            </div>
                          </InvoiceContainer>
                        </div>
                      );
                    })}
                </div>
              </InvoicesContainer>
            </ContainerConfigurate>
          </ContainerScroll>
        </Sidebar>
      )}

      <Authorization actions={["invoice.update_invoice_upfront_manual"]}>
        {showSidebar && (
          <AdminPaymentValidationSidebar
            handleAskForAction={handleAskForAction}
            handleRejectionComment={handleRejectionComment}
            onOutsideClick={() => updateShowSidebar(false)}
            sidebarInfo={sidebarInfo}
            setShowSidebarModal={setShowSidebarModal}
          />
        )}
        {showStateSidebar && (
          <AdminPaymentValidationStatesSidebar
            respData={respData}
            handleAskForAction={handleAskForAction}
            handleRejectionComment={handleRejectionComment}
            sidebarInfo={sidebarInfo}
            onOutsideClick={() => updateShowStateSidebar(false)}
            setShowSidebarModal={setShowSidebarModal}
            onErrorEndpoint={(error) => {
              const errorMessage = getErrorValueResponse(error);
              updateMessageError(errorMessage);
              toggleGetModal();
            }}
            onOk={() => {
              updateShowStateSidebar(false);
              const parsedParams = {
                ...parse(window.location.search),
                page: 1,
              };
              const stringifiedParams = stringify(parsedParams);
              const pathname = history.location.pathname;
              history.push(`${pathname}?${stringifiedParams}`);
            }}
          />
        )}
        {showRejectionComment && (
          <RejectionComment
            toggleModal={toggleShowRejectionComment}
            title={`Rechazar Pago N° ${selectedElement.invoiceNum}`}
            onReject={(comment: string) =>
              approveRejectDocuments(
                selectedElement,
                [],
                selectedStatus,
                comment
              )
            }
            hideSidebar={() => updateShowStateSidebar(false)}
          />
        )}
        {showAskForAction && (
          <AdminAskForAction
            toggleModal={toggleShowAskForAction}
            onApprove={() =>
              approveRejectDocuments(
                selectedElement,
                listVouchers,
                selectedStatus
              )
            }
            hideSidebar={() => updateShowStateSidebar(false)}
            titleMessage={"Confirmación de Aprobación"}
            bodyMessage={"El pago será aprobado al momento de confirmación."}
            buttonMessage={"Aprobar pago"}
          />
        )}
      </Authorization>
    </div>
  );
};

export default AdminPaymentValidationTable;
